import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Menu,
    MenuItem,
    Collapse,
    useTheme,
    useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link, useNavigate } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import PublicIcon from '@mui/icons-material/Public';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';

const bookingSubItems = [
    { text: 'Mabar', icon: <GroupIcon />, path: '/' },
    { text: 'Member', icon: <PersonIcon />, path: '/member' },
    { text: 'Coaching Clinic', icon: <SchoolIcon />, path: '/coaching-clinic' },
];

const communitySubItems = [
    { text: 'Friendly Match', icon: <SportsSoccerIcon />, path: '/friendly-match' },
    { text: 'Sport Tourism', icon: <FlightTakeoffIcon />, path: '/sport-tourism' },
];

const tournamentCenterSubItems = [
    { text: 'Ranked Match', icon: <TrendingUpIcon />, path: '/ranked-match' },
    { text: 'Local Ranked Match', icon: <TrendingUpIcon />, path: '/local-ranked-match' },
    { text: 'Internal Tournament', icon: <MilitaryTechIcon />, path: '/internal-tournament' },
    { text: 'Open Tournament', icon: <EmojiEventsIcon />, path: '/open-tournament' },
    { text: 'Liga', icon: <BarChartIcon />, path: '/liga' },
    { text: 'Live Score', icon: <SportsScoreIcon />, path: '/update-score' },
    { text: 'Bracket Generator', icon: <AccountTreeIcon />, path: '/bracket-generator' },
    { text: 'Matchmaking', icon: <PeopleIcon />, path: '/matchmaking' },
    { text: 'Leveling', icon: <PublicIcon />, path: '/leveling' },
];

const menuItems = [
    { text: 'Gallery', icon: <PhotoLibraryIcon />, path: '/gallery' },
    { text: 'Marketplace', icon: <ShoppingCartIcon />, path: '/marketplace' },
    { text: 'Tentang RaketerS', icon: <InfoIcon />, path: '/tentang-raketers' }
];

interface NavbarProps {
    mode: 'light' | 'dark';
    toggleColorMode: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ mode, toggleColorMode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallScreen = useMediaQuery('(max-width: 1440px)');
    const brandGap = isMobile ? 1.5 : 2;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [bookingAnchorEl, setBookingAnchorEl] = useState<null | HTMLElement>(null);
    const [communityAnchorEl, setCommunityAnchorEl] = useState<null | HTMLElement>(null);
    const [tournamentAnchorEl, setTournamentAnchorEl] = useState<null | HTMLElement>(null);
    const [bookingDrawerExpanded, setBookingDrawerExpanded] = useState(false);
    const [communityDrawerExpanded, setCommunityDrawerExpanded] = useState(false);
    const [tournamentDrawerExpanded, setTournamentDrawerExpanded] = useState(false);
    const navigate = useNavigate();

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleMenuClick = (path: string) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleBookingMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        // Tutup dropdown Tournament Center ketika hover ke Booking
        setTournamentAnchorEl(null);
        setBookingAnchorEl(e.currentTarget);
    };

    const handleBookingMouseLeave = () => {
        setBookingAnchorEl(null);
    };

    const handleBookingMenuEnter = () => {
        // no-op, hanya untuk mencegah penutupan instan saat masuk ke area menu
    };

    const handleBookingMenuLeave = () => {
        setBookingAnchorEl(null);
    };

    const handleTournamentMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        // Tutup dropdown Booking ketika hover ke Tournament Center
        setBookingAnchorEl(null);
        setTournamentAnchorEl(e.currentTarget);
    };

    const handleTournamentMouseLeave = () => {
        setTournamentAnchorEl(null);
    };

    const handleTournamentMenuEnter = () => {
        // no-op, hanya untuk mencegah penutupan instan saat masuk ke area menu
    };

    const handleTournamentMenuLeave = () => {
        setTournamentAnchorEl(null);
    };

    const handleCommunityMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        setBookingAnchorEl(null);
        setTournamentAnchorEl(null);
        setCommunityAnchorEl(e.currentTarget);
    };

    const handleCommunityMouseLeave = () => {
        setCommunityAnchorEl(null);
    };

    const handleCommunityMenuEnter = () => {
        // no-op
    };

    const handleCommunityMenuLeave = () => {
        setCommunityAnchorEl(null);
    };

    return (
        <>
            <AppBar position="fixed" color="default" sx={{ 
                top: 0,
                zIndex: (theme) => theme.zIndex.drawer - 1,
                left: 0,
                right: 0,
                background: mode === 'dark' 
                    ? '#0a1626' // warna langit malam (tidak biru terang)
                    : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                boxShadow: mode === 'dark' 
                    ? '0 8px 32px rgba(10, 22, 38, 0.6)'
                    : '0 8px 32px rgba(25, 118, 210, 0.3)',
                backdropFilter: 'blur(10px)',
                border: mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(255,255,255,0.2)',
                pl: 0,
                ...(mode === 'dark' ? {
                    backgroundImage:
                        `radial-gradient(circle at 10% 60%, rgba(255,255,255,0.13) 1.1px, transparent 1.6px),
                         radial-gradient(circle at 90% 30%, rgba(255,255,255,0.08) 1px, transparent 1.4px),
                         radial-gradient(circle at 60% 18%, rgba(255,255,255,0.14) 1.1px, transparent 1.7px)`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.6), transparent)'
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(99,170,255,0.6), transparent)'
                    },
                } : {})
            }}>
                <Toolbar sx={{ pl: (isMobile || isSmallScreen) ? brandGap : 0, minHeight: 56 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
                        <Link
                            to="/"
                            aria-label="Mabar Badminton RaketerS"
                            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: 0, marginLeft: 0 }}
                        >
                            <Box
                                component="img"
                                src="/images/Logo_trans.png"
                                alt=""
                                sx={{ height: 40, mr: brandGap, ml: 0 }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    width: 'max-content',
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        color: '#fff',
                                        fontWeight: 600,
                                        lineHeight: 1.15,
                                        whiteSpace: 'nowrap',
                                        fontSize: isMobile ? '0.62rem' : isSmallScreen ? '0.72rem' : '0.82rem',
                                    }}
                                >
                                    Mabar Badminton
                                </Typography>
                                <Box
                                    aria-hidden
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        color: '#fff',
                                        fontWeight: 700,
                                        lineHeight: 1.15,
                                        fontSize: isMobile ? '1.05rem' : isSmallScreen ? '1.2rem' : '1.35rem',
                                    }}
                                >
                                    {'RaketerS'.split('').map((char, index) => (
                                        <Box component="span" key={`${char}-${index}`}>
                                            {char}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Link>
                        <IconButton 
                            sx={{ 
                                ml: isMobile ? 0.2 : 1.5, 
                                color: '#fff',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.1)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                }
                            }} 
                            onClick={toggleColorMode}
                        >
                            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    {(isMobile || isSmallScreen) && (
                        <IconButton 
                            edge="end" 
                            color="inherit" 
                            aria-label="menu" 
                            onClick={handleDrawerToggle} 
                            sx={{ 
                                ml: 1,
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.1)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    {!isMobile && !isSmallScreen && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box
                                onMouseEnter={handleBookingMouseEnter}
                                onMouseLeave={handleBookingMouseLeave}
                            >
                                <Button
                                    color="inherit"
                                    startIcon={<EventAvailableIcon />}
                                    endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        minWidth: 'auto',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'rgba(255,255,255,0.1)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    BOOKING
                                </Button>
                                <Menu
                                    anchorEl={bookingAnchorEl}
                                    open={Boolean(bookingAnchorEl)}
                                    onClose={() => setBookingAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    sx={{ mt: 1.5 }}
                                    MenuListProps={{
                                        onMouseEnter: handleBookingMenuEnter,
                                        onMouseLeave: handleBookingMenuLeave,
                                        sx: { py: 0 }
                                    }}
                                    PaperProps={{
                                        onMouseEnter: handleBookingMenuEnter,
                                        onMouseLeave: handleBookingMenuLeave,
                                        sx: {
                                            background: mode === 'dark' ? '#0a1626' : '#fff',
                                            border: mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : '1px solid rgba(0,0,0,0.1)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    {bookingSubItems.map((item) => (
                                        <MenuItem
                                            key={item.text}
                                            component={Link}
                                            to={item.path}
                                            sx={{
                                                gap: 1.5,
                                                py: 1.5,
                                                color: mode === 'dark' ? '#fff' : 'text.primary',
                                            }}
                                        >
                                            {item.icon}
                                            {item.text}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            <Box
                                onMouseEnter={handleTournamentMouseEnter}
                                onMouseLeave={handleTournamentMouseLeave}
                            >
                                <Button
                                    color="inherit"
                                    startIcon={<EmojiEventsIcon />}
                                    endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        minWidth: 'auto',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'rgba(255,255,255,0.1)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    TOURNAMENT CENTER
                                </Button>
                                <Menu
                                    anchorEl={tournamentAnchorEl}
                                    open={Boolean(tournamentAnchorEl)}
                                    onClose={() => setTournamentAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    sx={{ mt: 1.5 }}
                                    MenuListProps={{
                                        onMouseEnter: handleTournamentMenuEnter,
                                        onMouseLeave: handleTournamentMenuLeave,
                                        sx: { py: 0 }
                                    }}
                                    PaperProps={{
                                        onMouseEnter: handleTournamentMenuEnter,
                                        onMouseLeave: handleTournamentMenuLeave,
                                        sx: {
                                            background: mode === 'dark' ? '#0a1626' : '#fff',
                                            border: mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : '1px solid rgba(0,0,0,0.1)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    {tournamentCenterSubItems.map((item) => (
                                        <MenuItem
                                            key={item.text}
                                            component={Link}
                                            to={item.path}
                                            sx={{
                                                gap: 1.5,
                                                py: 1.5,
                                                color: mode === 'dark' ? '#fff' : 'text.primary',
                                            }}
                                        >
                                            {item.icon}
                                            {item.text}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            <Box
                                onMouseEnter={handleCommunityMouseEnter}
                                onMouseLeave={handleCommunityMouseLeave}
                            >
                                <Button
                                    color="inherit"
                                    startIcon={<PeopleAltIcon />}
                                    endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        minWidth: 'auto',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'rgba(255,255,255,0.1)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    COMMUNITY
                                </Button>
                                <Menu
                                    anchorEl={communityAnchorEl}
                                    open={Boolean(communityAnchorEl)}
                                    onClose={() => setCommunityAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    sx={{ mt: 1.5 }}
                                    MenuListProps={{
                                        onMouseEnter: handleCommunityMenuEnter,
                                        onMouseLeave: handleCommunityMenuLeave,
                                        sx: { py: 0 }
                                    }}
                                    PaperProps={{
                                        onMouseEnter: handleCommunityMenuEnter,
                                        onMouseLeave: handleCommunityMenuLeave,
                                        sx: {
                                            background: mode === 'dark' ? '#0a1626' : '#fff',
                                            border: mode === 'dark' ? '1px solid rgba(99,170,255,0.22)' : '1px solid rgba(0,0,0,0.1)',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    {communitySubItems.map((item) => (
                                        <MenuItem
                                            key={item.text}
                                            component={Link}
                                            to={item.path}
                                            sx={{
                                                gap: 1.5,
                                                py: 1.5,
                                                color: mode === 'dark' ? '#fff' : 'text.primary',
                                            }}
                                        >
                                            {item.icon}
                                            {item.text}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            {menuItems.map((item) => (
                                <Button
                                    key={item.text}
                                    color="inherit"
                                    startIcon={item.icon}
                                    component={Link}
                                    to={item.path}
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#fff',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        minWidth: 'auto',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'rgba(255,255,255,0.1)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        }
                                    }}
                                >
                                    {item.text.toUpperCase()}
                                </Button>
                            ))}
                            <Button
                                color="inherit"
                                startIcon={<PersonIcon />}
                                component={Link}
                                to="/profile"
                                sx={{ 
                                    fontWeight: 600, 
                                    color: '#fff',
                                    fontSize: '0.8rem',
                                    px: 1,
                                    minWidth: 'auto',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.1)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    }
                                }}
                            >
                                PROFILE
                            </Button>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle}>
                <Box sx={{ 
                    width: 240,
                    background: mode === 'dark'
                        ? '#0a1626'
                        : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    ...(mode === 'dark' ? {
                        backgroundImage:
                            `radial-gradient(circle at 12% 24%, rgba(255,255,255,0.14) 1px, transparent 1.6px),
                             radial-gradient(circle at 78% 66%, rgba(255,255,255,0.10) 1px, transparent 1.6px),
                             radial-gradient(circle at 44% 82%, rgba(255,255,255,0.12) 1px, transparent 1.6px)`,
                        borderRight: '1px solid rgba(99,170,255,0.22)'
                    } : {}),
                    height: '100%',
                    color: '#fff'
                }} role="presentation" onClick={handleDrawerToggle}>
                    <List>
                        <ListItem 
                            button 
                            onClick={(e) => { e.stopPropagation(); setBookingDrawerExpanded(!bookingDrawerExpanded); }}
                            sx={{
                                '&:hover': {
                                    background: mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    mx: 1,
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: '#fff' }}><EventAvailableIcon /></ListItemIcon>
                            <ListItemText primary="Booking" sx={{ color: '#fff' }} />
                            {bookingDrawerExpanded ? <ExpandLessIcon sx={{ color: '#fff' }} /> : <ExpandMoreIcon sx={{ color: '#fff' }} />}
                        </ListItem>
                        <Collapse in={bookingDrawerExpanded} timeout="auto" unmountOnExit>
                            <List disablePadding>
                                {bookingSubItems.map((item) => (
                                    <ListItem 
                                        button 
                                        key={item.text} 
                                        onClick={() => handleMenuClick(item.path)}
                                        sx={{
                                            pl: 4,
                                            '&:hover': {
                                                background: mode === 'dark'
                                                    ? 'rgba(255,255,255,0.06)'
                                                    : 'rgba(255,255,255,0.15)',
                                                borderRadius: 2,
                                                mx: 1,
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#fff', minWidth: 36 }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} sx={{ color: '#fff' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                        <ListItem 
                            button 
                            onClick={(e) => { e.stopPropagation(); setTournamentDrawerExpanded(!tournamentDrawerExpanded); }}
                            sx={{
                                '&:hover': {
                                    background: mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    mx: 1,
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: '#fff' }}><EmojiEventsIcon /></ListItemIcon>
                            <ListItemText primary="Tournament Center" sx={{ color: '#fff' }} />
                            {tournamentDrawerExpanded ? <ExpandLessIcon sx={{ color: '#fff' }} /> : <ExpandMoreIcon sx={{ color: '#fff' }} />}
                        </ListItem>
                        <Collapse in={tournamentDrawerExpanded} timeout="auto" unmountOnExit>
                            <List disablePadding>
                                {tournamentCenterSubItems.map((item) => (
                                    <ListItem 
                                        button 
                                        key={item.text} 
                                        onClick={() => handleMenuClick(item.path)}
                                        sx={{
                                            pl: 4,
                                            '&:hover': {
                                                background: mode === 'dark'
                                                    ? 'rgba(255,255,255,0.06)'
                                                    : 'rgba(255,255,255,0.15)',
                                                borderRadius: 2,
                                                mx: 1,
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#fff', minWidth: 36 }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} sx={{ color: '#fff' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                        <ListItem 
                            button 
                            onClick={(e) => { e.stopPropagation(); setCommunityDrawerExpanded(!communityDrawerExpanded); }}
                            sx={{
                                '&:hover': {
                                    background: mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    mx: 1,
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: '#fff' }}><PeopleAltIcon /></ListItemIcon>
                            <ListItemText primary="Community" sx={{ color: '#fff' }} />
                            {communityDrawerExpanded ? <ExpandLessIcon sx={{ color: '#fff' }} /> : <ExpandMoreIcon sx={{ color: '#fff' }} />}
                        </ListItem>
                        <Collapse in={communityDrawerExpanded} timeout="auto" unmountOnExit>
                            <List disablePadding>
                                {communitySubItems.map((item) => (
                                    <ListItem 
                                        button 
                                        key={item.text} 
                                        onClick={() => handleMenuClick(item.path)}
                                        sx={{
                                            pl: 4,
                                            '&:hover': {
                                                background: mode === 'dark'
                                                    ? 'rgba(255,255,255,0.06)'
                                                    : 'rgba(255,255,255,0.15)',
                                                borderRadius: 2,
                                                mx: 1,
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: '#fff', minWidth: 36 }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} sx={{ color: '#fff' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                        {menuItems.map((item) => (
                            <ListItem 
                                button 
                                key={item.text} 
                                onClick={() => handleMenuClick(item.path)}
                                sx={{
                                    '&:hover': {
                                        background: mode === 'dark'
                                            ? 'rgba(255,255,255,0.08)'
                                            : 'rgba(255,255,255,0.2)',
                                        borderRadius: 2,
                                        mx: 1,
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ color: '#fff' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} sx={{ color: '#fff' }} />
                            </ListItem>
                        ))}
                        <ListItem 
                            button
                            onClick={() => handleMenuClick('/profile')}
                            sx={{
                                '&:hover': {
                                    background: mode === 'dark'
                                        ? 'rgba(255,255,255,0.08)'
                                        : 'rgba(255,255,255,0.2)',
                                    borderRadius: 2,
                                    mx: 1,
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: '#fff' }}><PersonIcon /></ListItemIcon>
                            <ListItemText primary="Profile" sx={{ color: '#fff' }} />
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Navbar; 